import type { Thunk } from 'types/redux'
import type { ServerBusinessInvite } from 'types/user'
import api from '@disco/api/server'
import {
  BUSINESS_INVITE_CANCEL,
  BUSINESS_INVITE_CANCEL_FAIL,
  BUSINESS_INVITE_CANCEL_SUCCESS,
  BUSINESS_INVITE_LOAD,
  BUSINESS_INVITE_LOAD_FAIL,
  BUSINESS_INVITE_LOAD_SUCCESS,
  BUSINESS_INVITE_RESEND,
  BUSINESS_INVITE_RESEND_FAIL,
  BUSINESS_INVITE_RESEND_SUCCESS,
  BUSINESS_INVITE_SEND,
  BUSINESS_INVITE_SEND_FAIL,
  BUSINESS_INVITE_SEND_SUCCESS,
  BUSINESS_USERS_CHANGE,
  BUSINESS_USERS_CHANGE_FAIL,
  BUSINESS_USERS_CHANGE_SUCCESS,
  BUSINESS_USERS_LOAD,
  BUSINESS_USERS_LOAD_FAIL,
  BUSINESS_USERS_LOAD_SUCCESS,
  BUSINESS_USERS_REMOVE,
  BUSINESS_USERS_REMOVE_FAIL,
  BUSINESS_USERS_REMOVE_SUCCESS,
} from '@disco/constants/ActionTypes'
import { pushUINotification } from '@disco/utils/notifications'
/**
 * Loads all business users from the server.
 * @returns Thunk that dispatches loading states and user data on success
 */
export function loadBusinessUsers(): Thunk {
  return async (dispatch, getState) => {
    const businessUsers = getState().entities.businessUsers
    if (businessUsers.loading) return

    try {
      dispatch({
        type: BUSINESS_USERS_LOAD,
      })
      const users = await api.loadBusinessUsers()

      if (users) {
        dispatch({
          type: BUSINESS_USERS_LOAD_SUCCESS,
          businessUsers: users,
        })
      } else {
        dispatch({
          type: BUSINESS_USERS_LOAD_FAIL,
          error: new Error('api.loadBusinessUsers() return undefined'),
        })
      }
    } catch (error) {
      dispatch({
        type: BUSINESS_USERS_LOAD_FAIL,
        error,
      })
      throw error
    }
  }
}
/**
 * Updates a business user's information with the provided changes.
 * @param userId - The ID of the user to update
 * @param changes - Object containing the changes to apply
 * @returns Thunk that dispatches change states and shows error notifications on failure
 */
export function changeBusinessUser(userId: number, changes: object): Thunk {
  return async dispatch => {
    try {
      dispatch({
        type: BUSINESS_USERS_CHANGE,
        userId: userId,
      })
      await api.changeBusinessUser(userId, changes)
      dispatch({
        type: BUSINESS_USERS_CHANGE_SUCCESS,
        userId: userId,
        changes,
      })
    } catch (error) {
      pushUINotification({
        message: `Sorry! A server error prevented this user from changing. Please try again, and if it fails, let us know.`,
        level: 'error',
      })
      dispatch({
        type: BUSINESS_USERS_CHANGE_FAIL,
        userId: userId,
        error,
      })
    }
  }
}
/**
 * Removes a business user from the system.
 * @param userId - The ID of the user to remove
 * @returns Thunk that dispatches removal states and shows error notifications on failure
 */
export function removeBusinessUser(userId: number): Thunk {
  return async dispatch => {
    try {
      dispatch({
        type: BUSINESS_USERS_REMOVE,
        userId: userId,
      })
      await api.removeBusinessUser(userId)
      dispatch({
        type: BUSINESS_USERS_REMOVE_SUCCESS,
        userId: userId,
      })
    } catch (error) {
      pushUINotification({
        message: `Sorry! A server error prevented this user from removing. Please try again, and if it fails, let us know.`,
        level: 'error',
      })
      dispatch({
        type: BUSINESS_USERS_REMOVE_FAIL,
        userId: userId,
        error,
      })
    }
  }
}
/**
 * Loads all pending business invitations from the server.
 * @returns Thunk that dispatches loading states and invite data on success
 */
export function loadBusinessInvites(): Thunk {
  return async dispatch => {
    try {
      dispatch({
        type: BUSINESS_INVITE_LOAD,
      })
      const invites = await api.loadBusinessInvites()
      dispatch({
        type: BUSINESS_INVITE_LOAD_SUCCESS,
        businessInvites: invites,
      })
    } catch (error) {
      dispatch({
        type: BUSINESS_INVITE_LOAD_FAIL,
        error,
      })
      throw error
    }
  }
}
/**
 * Sends business invitations to the specified users.
 * @param invites - Array of business invite objects to send
 * @returns Thunk that dispatches send states and returns invite response data
 */
export function sendBusinessInvite(invites: ServerBusinessInvite[]): Thunk {
  return async dispatch => {
    try {
      dispatch({
        type: BUSINESS_INVITE_SEND,
      })
      const invitesRes = await api.sendBusinessInvite(invites)
      dispatch({
        type: BUSINESS_INVITE_SEND_SUCCESS,
        businessInvites: invitesRes.invites,
      })
      return invitesRes
    } catch (error) {
      dispatch({
        type: BUSINESS_INVITE_SEND_FAIL,
        error,
      })
      throw error
    }
  }
}
/**
 * Resends a business invitation to a user.
 * @param inviteId - The ID of the invitation to resend
 * @returns Thunk that dispatches resend states and shows error notifications on failure
 */
export function resendBusinessInvite(inviteId: number): Thunk {
  return async (dispatch, getState) => {
    try {
      dispatch({
        type: BUSINESS_INVITE_RESEND,
        businessInviteId: inviteId,
      })
      const invite = getState().entities.businessInvites.invites[inviteId]
      await api.resendBusinessInvite(inviteId, invite)
      dispatch({
        type: BUSINESS_INVITE_RESEND_SUCCESS,
        businessInviteId: inviteId,
      })
    } catch (error) {
      pushUINotification({
        message: `Sorry! A server error prevented this invite from resending. Please try again, and if it fails, let us know.`,
        level: 'error',
      })
      dispatch({
        type: BUSINESS_INVITE_RESEND_FAIL,
        businessInviteId: inviteId,
        error,
      })
    }
  }
}
/**
 * Cancels a pending business invitation.
 * @param inviteId - The ID of the invitation to cancel
 * @returns Thunk that dispatches cancel states and shows error notifications on failure
 */
export function cancelBusinessInvite(inviteId: number): Thunk {
  return async dispatch => {
    try {
      dispatch({
        type: BUSINESS_INVITE_CANCEL,
        businessInviteId: inviteId,
      })
      await api.cancelBusinessInvite(inviteId)
      dispatch({
        type: BUSINESS_INVITE_CANCEL_SUCCESS,
        businessInviteId: inviteId,
      })
    } catch (error) {
      pushUINotification({
        message: `Sorry! A server error prevented this invite from cancelling. Please try again, and if it fails, let us know.`,
        level: 'error',
      })
      dispatch({
        type: BUSINESS_INVITE_CANCEL_FAIL,
        businessInviteId: inviteId,
        error,
      })
    }
  }
}
